import type {
  EditorialEditorModel,
  FinanceEditorModel,
  ServiceEditorModel,
  ReferenceDomainEditorModel,
  ReferenceCompanyDomainEditorModel,
} from "./editor";

export type EditorConflictModel =
  | ReferenceDomainEditorModel
  | ReferenceCompanyDomainEditorModel
  | ServiceEditorModel
  | FinanceEditorModel
  | EditorialEditorModel;

export type EditorConflictSection =
  | "record"
  | "translation"
  | "content"
  | "relations";

export type EditorConflictChoice = "local" | "server";

export type EditorConflictRow = {
  section: EditorConflictSection;
  label: string;
  localChanged: boolean;
  serverChanged: boolean;
  requiresChoice: boolean;
};

const sectionLabels: Record<EditorConflictSection, string> = {
  record: "Yapı ve temel ayarlar",
  translation: "Başlık ve metinler",
  content: "Detay içeriği",
  relations: "İlişkiler",
};

const sections: readonly EditorConflictSection[] = [
  "record",
  "translation",
  "content",
  "relations",
];

function sameValue(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function sectionValue(
  model: EditorConflictModel,
  section: EditorConflictSection,
): unknown {
  if (section === "relations") {
    return "relations" in model ? model.relations : undefined;
  }
  return model[section];
}

export function editorConflictRows(
  base: EditorConflictModel,
  local: EditorConflictModel,
  server: EditorConflictModel,
): EditorConflictRow[] {
  return sections.flatMap((section) => {
    const localChanged = !sameValue(
      sectionValue(base, section),
      sectionValue(local, section),
    );
    const serverChanged = !sameValue(
      sectionValue(base, section),
      sectionValue(server, section),
    );
    if (!localChanged && !serverChanged) return [];
    return [
      {
        section,
        label: sectionLabels[section],
        localChanged,
        serverChanged,
        requiresChoice: localChanged && serverChanged,
      },
    ];
  });
}

export function mergeEditorConflict(
  base: EditorConflictModel,
  local: EditorConflictModel,
  server: EditorConflictModel,
  choices: Partial<Record<EditorConflictSection, EditorConflictChoice>>,
): EditorConflictModel {
  if (base.kind !== local.kind || local.kind !== server.kind) {
    throw new Error("Çakışan editör kayıtlarının türü eşleşmiyor.");
  }
  const merged = { ...server } as EditorConflictModel;
  for (const row of editorConflictRows(base, local, server)) {
    if (
      (row.localChanged && !row.serverChanged) ||
      (row.requiresChoice && choices[row.section] === "local")
    ) {
      Object.assign(merged, {
        [row.section]: sectionValue(local, row.section),
      });
    }
  }
  return merged;
}
